import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { capitalize } from '../../../utils/utils';
import UploadFileIcon from '../../Icons/UploadFileIcon';
import { FieldErrorInfo, InputWrapper } from '../FormField';
import ExcelPresentationTable from './ExcelPresentationTable';

export interface UploadExcelFileFieldProps {
  className?: string;
  label: string;
  placeholder?: string;
  id?: string;
  name: string;
}

const UploadExcelFileField: React.FC<UploadExcelFileFieldProps> = ({
  className,
  label,
  placeholder,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const [fileList, setFileList] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { handleBlur } = useFormikContext<any>();

  useEffect(() => {
    if (field.value) {
      const files = Array.from(field.value) as File[];
      setFileList(files);
    } else {
      setFileList([]);
    }
  }, [field.value]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files && event.target.files;

    if (files) {
      const fileArray = Array.from(files);
      helpers.setValue(fileArray);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files) {
      const fileArray = Array.from(files);
      helpers.setValue(fileArray);
    }
  };

  return (
    <InputWrapper className={className}>
      <DocumentInputField
        multiple
        name={props.name}
        id={props.name}
        type='file'
        accept='.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        onChange={handleFileChange}
        onBlur={handleBlur}
      />

      {fileList?.length < 1 ? (
        <Label htmlFor={props.name || props.id}>
          <UploadBox
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <UploadFileIcon />
            <Text>
              Drag and Drop or <Browse>Browse</Browse>
            </Text>
            <Text>Supported format: .XLSX</Text>
          </UploadBox>
        </Label>
      ) : (
        <ExcelPresentationTable csvFile={field.value} />
      )}
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{capitalize(meta?.error)}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default UploadExcelFileField;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DocumentInputField = styled.input`
  display: none;
`;

const UploadBox = styled.div<{ isDragging: boolean }>`
  display: flex;
  gap: 12px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 72px;
  cursor: pointer;
  background-color: ${({ isDragging }) =>
    isDragging ? 'rgba(0, 128, 255, 0.1)' : 'transparent'};
`;

const Browse = styled.span`
  color: ${({ theme }) => theme.palette.blueColor};
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
`;

const Text = styled.div`
  color: ${({ theme }) => theme.palette.mediumGrayText};
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
`;
